import * as React from 'react';
import {
  AutoSizer,
  Index,
  List as VirtualizedList,
  ListRowRenderer,
  CellMeasurerCache,
} from 'react-virtualized';
import { RenderedRows } from 'react-virtualized/dist/es/List';
import styled from 'styled-components';
import { ISizer } from '../../../data/types';
import { ListItem, ListItemCell, ListItemSafeText } from './ListItem';

export interface IListRowRenderProps {
  index: number;
  key: string;
  style: React.CSSProperties;
}

interface IProps {
  cache?: CellMeasurerCache;
  listLabel?: string;
  listLength: number;
  onRowsRendered?: (info: RenderedRows) => void;
  renderOnChange?: unknown[];
  role?: string;
  roundedContainer?: boolean;
  rowHeight?: number | ((info: Index) => number);
  rowRenderer: ListRowRenderer;
  scrollTo?: number;
  stickyHeading?: string;
  styles?: React.CSSProperties;
}

export const List = (props: IProps): React.ReactElement => {
  return (
    <ListContainer aria-label={props.listLabel}>
      <AutoSizer>
        {(size: ISizer): React.ReactElement => {
          const { width, height } = size;
          return (
            <React.Fragment>
              {props.stickyHeading && (
                <ListSectionHeading
                  role='row'
                  roundedContainer={props.roundedContainer}
                  atTop
                  style={{
                    position: 'fixed',
                    width: width - 15,
                    height:
                      typeof props.rowHeight === 'function'
                        ? props.rowHeight({ index: 0 })
                        : props.rowHeight,
                  }}
                  label={props.stickyHeading}
                />
              )}
              <VirtualizedList
                style={{ outline: 'none', outlineWidth: 0, ...props.styles }}
                height={height}
                width={width}
                rowHeight={props.rowHeight}
                deferredMeasurementCache={props.cache}
                rowCount={props.listLength}
                onRowsRendered={props.onRowsRendered}
                rowRenderer={props.rowRenderer}
                data={props.renderOnChange}
                scrollToIndex={props.scrollTo}
                scrollToAlignment='center'
              />
            </React.Fragment>
          );
        }}
      </AutoSizer>
    </ListContainer>
  );
};

const ListContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;

interface ISectionHeadingProps {
  label: string;
  style?: React.CSSProperties;
  sticky?: boolean;
  roundedContainer?: boolean;
  atTop?: boolean;
  role?: string;
}

/**
 * @description section header for grouped lists
 * @param props
 */
export const ListSectionHeading = (
  props: ISectionHeadingProps
): React.ReactElement => {
  return (
    <ListItem
      style={{
        ...props.style,
        backgroundColor: 'var(--color-surface-6)',
        borderTopLeftRadius: props.roundedContainer ? '10px' : '0px',
        zIndex: props.atTop ? 9 : 0,
      }}
    >
      <ListItemCell role='cell'>
        <ListItemSafeText>{props.label}</ListItemSafeText>
      </ListItemCell>
    </ListItem>
  );
};
